import React, { useEffect, useRef, useState } from 'react';
import { useLazyImageSrc } from 'hooks/useLazyImageSrc';
import { useAdaptiveScreenshotSrc } from 'hooks/useAdaptiveScreenshotSrc';

/**
 * Card screenshot image with optional visibility gating and adaptive tier loading.
 */
const LazyImage = ({
  src,
  when,
  lazy = true,
  rootMargin,
  adaptive,
  wrapperClassName,
  wrapperStyle,
  placeholder,
  onLoad,
  onError,
  onTierUpgrade,
  style,
  className,
  ...imgProps
}) => {
  const useObserver = lazy && when === undefined;
  const { ref, shouldLoad: observerLoad } = useLazyImageSrc({
    enabled: useObserver,
    rootMargin,
  });
  const visible = when !== undefined ? when : observerLoad;

  const adaptiveState = useAdaptiveScreenshotSrc({
    enabled: Boolean(adaptive),
    visible: adaptive ? visible : false,
    tiers: adaptive?.tiers,
    layoutWidth: adaptive?.layoutWidth,
    viewZoom: adaptive?.viewZoom,
    isHovered: adaptive?.isHovered,
    nativeWidth: adaptive?.nativeWidth,
    onTierUpgrade,
  });

  const adaptiveFallback = adaptive?.tiers?.thumb
    || adaptive?.tiers?.medium
    || adaptive?.tiers?.full;
  const fallbackSrc = visible
    ? (adaptive ? adaptiveFallback : src)
    : undefined;
  const resolvedSrc = adaptive
    ? (adaptiveState.src || fallbackSrc)
    : fallbackSrc;
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);
  const hadLoadedImageRef = useRef(false);

  useEffect(() => {
    const img = imgRef.current;
    if (!resolvedSrc) {
      setLoaded(false);
      hadLoadedImageRef.current = false;
      return;
    }
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
      hadLoadedImageRef.current = true;
      return;
    }
    // Keep the current image visible during adaptive tier upgrades.
    if (!hadLoadedImageRef.current) {
      setLoaded(false);
    }
  }, [resolvedSrc]);

  const handleLoad = event => {
    adaptiveState.onImageLoad?.(event);
    setLoaded(true);
    hadLoadedImageRef.current = true;
    onLoad?.(event);
  };

  const handleError = event => {
    setLoaded(false);
    onError?.(event);
  };

  return (
    <div
      ref={useObserver ? ref : undefined}
      className={wrapperClassName}
      style={{ position: 'relative', width: style?.width, ...wrapperStyle }}
    >
      {!loaded && placeholder}
      <img
        {...imgProps}
        ref={imgRef}
        className={className}
        src={resolvedSrc}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          ...style,
          display: style?.display ?? 'block',
          opacity: loaded ? 1 : 0,
        }}
      />
    </div>
  );
};

export default LazyImage;
